import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "unlimited-session-cart";

function readCart() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function makeItemId(item) {
  if (item.cartId) return item.cartId;
  if (item.type === "studio") return `studio-${item.date}`;
  if (item.type === "service") return `service-${item.serviceId}-${Date.now()}`;
  if (item.type === "latdior") return `latdior-${item.productId}-${item.size || "na"}`;
  return `${item.type || "item"}-${Date.now()}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readCart());

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    const nextItem = { ...item, cartId: makeItemId(item), quantity: item.quantity || 1 };
    setItems((current) => {
      if (nextItem.type === "studio") {
        return [...current.filter((entry) => entry.type !== "studio"), nextItem];
      }

      const existing = current.find((entry) => entry.cartId === nextItem.cartId);
      if (!existing) return [...current, nextItem];

      return current.map((entry) =>
        entry.cartId === nextItem.cartId
          ? { ...entry, quantity: Number(entry.quantity || 1) + Number(nextItem.quantity || 1) }
          : entry
      );
    });
  };

  const updateQuantity = (cartId, quantity) => {
    const safeQuantity = Math.max(1, Number(quantity || 1));
    setItems((current) =>
      current.map((item) => (item.cartId === cartId ? { ...item, quantity: safeQuantity } : item))
    );
  };

  const removeItem = (cartId) => {
    setItems((current) => current.filter((item) => item.cartId !== cartId));
  };

  const clearCart = () => setItems([]);

  const totals = useMemo(() => {
    const count = items.reduce((sum, item) => sum + Number(item.quantity || 1), 0);
    const total = items.reduce((sum, item) => {
      if (item.type === "studio") return sum + Number(item.total || 0);
      return sum + Number(item.unitPrice || 0) * Number(item.quantity || 1);
    }, 0);
    return { count, total };
  }, [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQuantity, removeItem, clearCart, totals }),
    [items, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
